/**
 * Direct-to-storage upload for presigned PUT URLs. Bytes go from the browser
 * to the blob store; the API only mints the URL and later claims the key.
 */

/** Azure requires the blob type on a PUT that creates the blob. */
export function isAzureBlobUrl(url: string): boolean {
  try {
    return new URL(url).hostname.endsWith('.blob.core.windows.net');
  } catch {
    return false;
  }
}

export function presignedUploadHeaders(
  uploadUrl: string,
  contentType: string,
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': contentType || 'application/octet-stream',
  };
  if (isAzureBlobUrl(uploadUrl)) headers['x-ms-blob-type'] = 'BlockBlob';
  return headers;
}

/** A stalled PUT must not leave the caller pending forever. */
export const PRESIGNED_UPLOAD_TIMEOUT_MS = 120_000;

export async function uploadBlobToPresignedUrl(
  uploadUrl: string,
  blob: Blob,
  contentType: string,
  fetchImpl: typeof fetch = fetch,
  signal: AbortSignal = AbortSignal.timeout(PRESIGNED_UPLOAD_TIMEOUT_MS),
): Promise<void> {
  const response = await fetchImpl(uploadUrl, {
    method: 'PUT',
    body: blob,
    headers: presignedUploadHeaders(uploadUrl, contentType),
    signal,
  });
  if (!response.ok) {
    throw new Error(
      `Failed to upload file: ${response.status} ${response.statusText}`,
    );
  }
}
